import React, {useState} from 'react';
import {Trans} from 'react-i18next';
import Section from '../../layout/Section';
import {getDeviceToLabelMap} from '../../config/utils/optionsUtils';
import langCode from '../../../../../utils/langUtils';
import DataTable from '../../../../../components/datatable/DataTable';
import Loader from '../../../../../components/spinner/Loader';
import {useSelector} from 'react-redux';
import {privilegeUtils} from '../../../../../utils/privilegeUtils';
import ContactSalesCard from '../../components/ContactSalesCard';
import _get from 'lodash/get';
import ElevatedButton from '../../../../../components/formik/ElevatedButton';
import {handleTemplateExport} from '../../../../../utils/skincareUtils';
import OverLayLoader from '../../../../../components/spinner/OverLayLoader';

export default function CustomizedLayoutSettings({
  data,
  isLoading,
  selectedTempInfo,
  handleSelectedTemplate,
  isTechnicalSupport,
}) {
  const [isExporting, setIsExporting] = useState(false);
  const auth = useSelector(state => state.components.auth.accountSummary);

  const handleExport = async (templateId, templateName) => {
    const sanitizedName = templateName
      .replace(/[^a-z0-9]/gi, '_')
      .toLowerCase();
    await handleTemplateExport({
      templateId,
      fileName: `SharedSkinResultPage_${sanitizedName}`,
      setIsExporting,
    });
  };

  if (!privilegeUtils.canCustomizationAppLayout(auth)) {
    return <ContactSalesCard />;
  }
  if (isLoading) {
    return <Loader />;
  }

  const columns = [
    {
      Header: (
        <Trans i18nKey="p.s.uiconfiguration.customized.ui.selected">
          Selected
        </Trans>
      ),
      accessor: 'template_id',
      disableSortBy: true,
      style: {width: '5%', textAlign: 'center'},
      Cell: ({
        cell: {
          row: {original},
        },
      }) => (
        <div className="text-center">
          <input
            type="radio"
            value={original.template_id}
            onChange={e => {
              handleSelectedTemplate(original);
            }}
            checked={
              _get(selectedTempInfo, 'template_id') === original.template_id
            }
          />
        </div>
      ),
    },
    {
      Header: (
        <Trans i18nKey="p.s.c.customization.template.name">Template Name</Trans>
      ),
      accessor: 'template_name',
      disableSortBy: true,
      style: {width: '10%', textAlign: 'center'},
    },
    {
      Header: <Trans i18nKey="p.c.listrequests.language">Language</Trans>,
      accessor: 'langs',
      disableSortBy: true,
      style: {width: '10%', textAlign: 'center'},
      Cell: ({cell: {value}}) => (
        <div style={{wordBreak: 'break-word'}}>
          {value && Array.isArray(value)
            ? value
                .map(
                  code =>
                    langCode.getConsoleStringLangToLabelMap()[code] || code
                )
                .join(', ')
            : '-'}
        </div>
      ),
    },
    {
      Header: <Trans i18nKey="p.g.d.device.tag.type">Device Type</Trans>,
      accessor: 'device_type',
      disableSortBy: true,
      style: {width: '10%', textAlign: 'center'},
      Cell: ({cell: {value}}) => (
        <div style={{wordBreak: 'break-word'}}>
          {value && Array.isArray(value)
            ? value.map((code, index, arr) => (
                <span key={code}>
                  {getDeviceToLabelMap()[code] || code}
                  {index < arr.length - 1 && ','}&nbsp;
                </span>
              ))
            : '-'}
        </div>
      ),
    },
    ...(isTechnicalSupport
      ? [
          {
            Header: <Trans i18nKey="p.s.c.customization.file">File</Trans>,
            accessor: 'file',
            disableSortBy: true,
            style: {width: '10%', textAlign: 'center'},
            Cell: ({
              cell: {
                row: {original},
              },
            }) => (
              <div className="text-center">
                <ElevatedButton
                  type="custom"
                  faIcon="fa-download"
                  text={
                    <Trans i18nKey="p.s.c.customization.export.html.file">
                      Export HTML File
                    </Trans>
                  }
                  onClick={() =>
                    handleExport(original.template_id, original.template_name)
                  }
                  disabled={isExporting}
                />
              </div>
            ),
          },
        ]
      : []),
  ];
  return (
    <div>
      <Section
        title={
          <Trans i18nKey="p.s.uiconfiguration.customized.ui.template">
            Template Info
          </Trans>
        }
      >
        <div>
          <Trans i18nKey="p.s.uiconfiguration.customized.ui.template.desc">
            Once a customized UI layout is selected, any future updates must be
            made through our support team. To regain access to standard settings
            and new features, you can switch back to the generic layout at any
            time.
          </Trans>
        </div>
        <DataTable
          columns={columns}
          data={data}
          initState={{
            pageSize: 5,
          }}
        />
      </Section>
      {isExporting && (
        <OverLayLoader
          text={
            <Trans i18nKey="c.spinner.overlay.loader.exporting">
              Exporting...
            </Trans>
          }
        />
      )}
    </div>
  );
}
